<template>
  <div class="b-vehicle-flexdrive-resume">
    <div class="b-vehicle-flexdrive-resume__header">
      <span class="b-vehicle-flexdrive-resume__title h2">
        {{ dictionary.vehicleResumeTitle }}
      </span>

      <Anchor
        v-if="editVehicleLink && showEditButton"
        :item="editVehicleLink"
        :hide-text="true"
        class="btn btn-link-light btn-icon-start btn-edit">
        <i class="icon-edit"></i>
        <small>{{ editVehicleLink.text }}</small>
      </Anchor>
    </div>
    <div class="">
      <div class="b-vehicle-flexdrive-resume__vehicle view-list">
        <div class="b-vehicle-flexdrive-resume__body">
          <div class="b-vehicle-flexdrive-resume__title h4 extrabold">
            {{ item.make }} {{ item.model }}
            <span v-if="!item?.isGuaranteed">
              {{ dictionary.orSimilarText }}
            </span>
          </div>
          <div class="b-vehicle-flexdrive-resume__details mb-24">
            <ul>
              <li v-if="item.transmissionType">
                <div class="semibold">
                  <span>
                    {{ item.transmissionType }}
                  </span>
                </div>
              </li>
              <li v-if="item.fuel" :class="{ 'is-electric': isElectric }">
                <div class="semibold">
                  <span>{{ item.fuel }}</span>
                </div>
              </li>
              <li v-if="emissions">
                <div class="semibold">
                  <span>{{ item.emissions }} &nbsp;gr/km</span>
                </div>
              </li>
              <li v-if="consumption">
                <div class="semibold">
                  <span>{{ item.consumption }} &nbsp;lt/100km</span>
                </div>
              </li>
              <li v-if="item.drivingRange > 0">
                <div class="semibold">
                  {{ dictionary.vehicleDrivingRangePrefix }}
                  {{ item.drivingRange.toLocaleString(lang) }}
                  {{ dictionary.vehicleDrivingRangeSuffix }}
                </div>
              </li>
            </ul>
          </div>
          <!-- </div> -->
          <div class="b-vehicle-flexdrive-resume__picture mb-30">
            <LazyImage v-if="image" :image="image" />
            <div v-if="item.isNew" class="e-new lottie">
              <NewLottie />
            </div>
            <div
              v-if="item.offerText"
              class="b-vehicle-flexdrive-resume__offer-text">
              {{ item.offerText }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="b-vehicle-flexdrive-resume__main">
      <div
        v-if="item.highlightText"
        class="b-vehicle-flexdrive-resume__separator--highlight">
        <div class="line"></div>
        <div class="title">{{ item.highlightText }}</div>
        <div class="line"></div>
      </div>
      <div v-else class="b-vehicle-flexdrive-resume__separator"></div>

      <div class="b-vehicle-flexdrive-resume__price">
        <div class="mt-10">
          {{ dictionary.vehicleResumeTotalCostLabel }}
        </div>
        <div>
          <div
            class="b-vehicle-flexdrive-resume__price-w-vat"
            :class="{ 'is-active': vatIncluded }">
            <div class="bold mb-5">
              <span>{{ dictionary.vehiclePricePrefix }}&nbsp;</span>
              <!-- prettier-ignore -->
              <span v-if="item.startingPricePerMonthInteger && !vatIncluded" class="e-discount-price">€{{ item.startingPricePerMonthInteger.toLocaleString(lang) }}</span>
              <!-- prettier-ignore -->
              <span v-if="item.startingPricePerMonthInclVatInteger  && vatIncluded" class="e-discount-price">€{{ item.startingPricePerMonthInclVatInteger.toLocaleString(lang) }}</span>
              <span v-if="item.startingPricePerMonthInteger">&nbsp;</span>
              <span class="animate">
                <em v-if="vatIncluded">{{ item.pricePerMonthInclVat }}</em>
                <em v-else>{{ item.pricePerMonth }}</em>
                {{ dictionary.vehiclePriceSuffix }}
              </span>
            </div>

            <div class="form-check form-switch">
              <label class="form-check-label" for="vatIncluded">
                <span v-if="vatIncluded">
                  {{ dictionary.vehicleVatIncluded }}
                </span>
                <span v-else>{{ dictionary.vehicleVatExcluded }}</span>
              </label>
              <input
                v-if="!isDisabled"
                id="vatIncluded"
                v-model="vatIncluded"
                class="form-check-input"
                type="checkbox"
                role="switch"
                @change="onToggleVat" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as lib from '../../../lib/lib'
import LazyImage from './../common/LazyImage.vue'
import Anchor from './../../components/common/Anchor.vue'
import constants from '../../../lib/constants'
import NewLottie from './../../components/lotties/New.vue'

export default {
  components: { LazyImage, Anchor, NewLottie },
  props: {
    item: {
      required: true,
      type: Object,
    },
    step: {
      required: false,
      type: Number,
      default: 0,
    },
    showEditButton: {
      required: false,
      type: Boolean,
      default: true,
    },
  },
  emits: ['on-toggle-vat'],
  data() {
    return {
      vatIncluded: false,
      editVehicleLink: null,
      lang: 'en',
    }
  },
  computed: {
    isElectric() {
      return this.item.fuelPreValue === constants.flexdrive.fuelType.electic
    },
    emissions() {
      if (!this.item.emissions || parseInt(this.item.emissions) <= 0) {
        return false
      }
      return true
    },
    consumption() {
      if (!this.item.consumption || parseInt(this.item.consumption) <= 0) {
        return false
      }
      return true
    },
    isDisabled() {
      if (!this.item) {
        return true
      }
      if (!this.item.pricePerMonthInclVat) {
        return true
      }

      return false
    },
    image() {
      if (!this.item) {
        return false
      }
      const images = this.item.images
      if (!images.length) {
        return null
      }
      return {
        url: images[0].url,
        alt: '',
      }
    },
    transmissionType() {
      if (
        this.item.transmissionTypePreValue ===
        constants.flexdrive.filters.transmissionsOptions.manual
      ) {
        return this.dictionary.transmissionManual
      }
      if (
        this.item.transmissionTypePreValue ===
        constants.flexdrive.filters.transmissionsOptions.auto
      ) {
        return this.dictionary.transmissionManual
      }

      return ''
    },
  },
  mounted() {
    const { editVehicleLink, vatIncluded } = lib.getData('flexDriveBookingData')
    this.lang = lib.getLocaleFromLangAttr()

    if (this.showEditButton) {
      const urlHashAdded = { url: `${editVehicleLink.url}#selectVehicle` }
      this.editVehicleLink = { ...editVehicleLink, ...urlHashAdded }
    }

    if (this.item.pricePerMonthInclVat) {
      this.vatIncluded = vatIncluded
    }
  },
  methods: {
    onToggleVat() {
      this.$emit('on-toggle-vat')
    },
  },
}
</script>
